import { memo, useEffect, useMemo, useState } from 'react';
import { Button, Card, Col, Image, Row } from 'react-bootstrap';
import type { LeadCloudFileType } from '@econtractor/types';
import { getFileExtension, isPreviewImage } from './helper/filesUtils';
import 'glightbox/dist/css/glightbox.min.css';
import SolarIcon from 'components/icons/SolarIcon';

interface FilesListProps {
    files: LeadCloudFileType[];
    galleryId: string;
    imageColProps?: {
        xs?: number;
        sm?: number;
        md?: number;
        lg?: number;
        xl?: number;
        xxl?: number;
    };
    canDelete?: boolean;
    deletingKey?: string | null;
    onDelete?: (file: LeadCloudFileType) => void;
}

const FilesList = memo(
    ({ files, galleryId, imageColProps, canDelete, deletingKey, onDelete }: FilesListProps) => {
        const imageFiles = useMemo(() => files.filter((file) => isPreviewImage(file.fileName)), [files]);
        const otherFiles = useMemo(() => files.filter((file) => !isPreviewImage(file.fileName)), [files]);
        const colProps = imageColProps || { xs: 6, md: 4 };

        useEffect(() => {
            const GLightbox = require('glightbox');
            const lightbox = GLightbox({
                selector: '.glightbox',
            });

            return () => lightbox.destroy();
        }, [imageFiles]);

        if (!imageFiles.length && !otherFiles.length) return null;

        return (
            <>
                {imageFiles.length > 0 && (
                    <Row>
                        {imageFiles.map((file) => (
                            <Col className="p-1" key={file.storageKey} {...colProps}>
                                <Card>
                                    <Card.Body className="p-1" style={{ minHeight: '40px' }}>
                                        {canDelete && (
                                            <div style={{ position: 'absolute', right: '4px', zIndex: 1 }}>
                                                <Button
                                                    variant="light-danger"
                                                    size="sm"
                                                    disabled={deletingKey === file.storageKey}
                                                    onClick={() => onDelete?.(file)}
                                                >
                                                    <SolarIcon name="trash3" />
                                                </Button>
                                            </div>
                                        )}
                                        <a
                                            className="glightbox"
                                            href={file.publicUrl}
                                            target="_blank"
                                            rel="noreferrer"
                                            data-gallery={galleryId}
                                            title={file.fileName}
                                        >
                                            <img className="img-fluid rounded" src={file.publicUrl} alt={file.fileName} />
                                        </a>
                                        <div className="text-truncate fs-12 text-muted mt-1 px-1" title={file.fileName}>
                                            {file.fileName}
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        ))}
                    </Row>
                )}

                {otherFiles.length > 0 && (
                    <div
                        className="_form-control bg-body mb-2 p-2 d-flex flex-wrap rounded mt-2 text-start"
                        style={{ gap: '10px' }}
                    >
                        <ul className="list-inline mb-0">
                            {otherFiles.map((file) => (
                                <OneFile
                                    key={file.storageKey}
                                    file={file}
                                    canDelete={canDelete}
                                    deleting={deletingKey === file.storageKey}
                                    onDelete={() => onDelete?.(file)}
                                />
                            ))}
                        </ul>
                    </div>
                )}
            </>
        );
    },
    (prevProps, nextProps) =>
        prevProps.files === nextProps.files &&
        prevProps.deletingKey === nextProps.deletingKey &&
        prevProps.canDelete === nextProps.canDelete &&
        prevProps.onDelete === nextProps.onDelete &&
        prevProps.galleryId === nextProps.galleryId &&
        prevProps.imageColProps === nextProps.imageColProps,
);

interface OneFileProps {
    file: LeadCloudFileType;
    canDelete?: boolean;
    deleting?: boolean;
    onDelete?: () => void;
}

const OneFile = ({ file, canDelete, deleting, onDelete }: OneFileProps) => {
    const extension = getFileExtension(file.fileName);
    const [src, setSrc] = useState(`/imgs/fileTypes/${extension}.png`);

    return (
        <li className="list-inline-item" style={{ maxWidth: '250px' }}>
            <Card className="p-1 my-1 text-truncate" style={{ borderRadius: '8px' }} title={file.fileName}>
                <Card.Body className="text-truncate p-0 d-flex align-items-center">
                    <Image
                        className="rounded img-fluid me-1"
                        style={{ height: '24px', padding: '2px' }}
                        onError={() => setSrc('/imgs/fileTypes/unknown.png')}
                        src={src}
                    />
                    <span className="text-truncate fs-12" style={{ paddingRight: canDelete ? '30px' : '10px' }}>
                        <a target="_blank" href={file.publicUrl} rel="noreferrer">
                            {file.fileName}
                        </a>
                    </span>
                    {canDelete && (
                        <div style={{ position: 'absolute', right: '8px', opacity: '0.8' }}>
                            <Button
                                className="px-1 py-0"
                                variant="light-danger"
                                size="sm"
                                disabled={deleting}
                                onClick={onDelete}
                            >
                                <SolarIcon name="trash3" className="fs-12" />
                            </Button>
                        </div>
                    )}
                </Card.Body>
            </Card>
        </li>
    );
};

export default FilesList;
